import { cloneDeep, find, isString, round, sortBy } from 'lodash';
import maxBy from 'lodash/maxBy';
import minBy from 'lodash/minBy';
import { trackConfig, type IFramework } from '@/framework';
import { getRotatePosition } from '@/utils/collision-kit';
import { pointsDistance } from '@/utils/math-kit';
import { LayerType, type ILayerProps, type IPoint } from '@/version-types';

interface IPointVariant extends IPoint {
    id?: string;
}

function getTrackFunc(type: string) {
    const groupAlign = trackConfig[LayerType.GROUP_LAYER]?.groupAlign;
    return isString(groupAlign) ? groupAlign : groupAlign?.[type];
}

export class GroupAlign {
    constructor(private framework: IFramework) {}

    /** 更新图层组信息 */
    private updateGroupLayer(layer: ILayerProps, positions: IPoint[]) {
        if (!positions.length) return;
        const minX = minBy(positions, (pos) => pos.x) as IPoint;
        const maxX = maxBy(positions, (pos) => pos.x) as IPoint;
        const minY = minBy(positions, (pos) => pos.y) as IPoint;
        const maxY = maxBy(positions, (pos) => pos.y) as IPoint;
        layer.left = minX.x;
        layer.top = minY.y;
        layer.width = maxX.x - minX.x;
        layer.height = maxY.y - minY.y;
        layer.rotate = 0;
        this.framework.service.frame.addOrUpdateLayer(layer);
        this.framework.service.frame.setActiveLayer(layer.id);
    }

    private updateChildrenLayer(layer: ILayerProps, offsetLeft = 0, offsetTop = 0) {
        (layer.children || []).forEach((item) => {
            item.left = item.left + offsetLeft;
            item.top = item.top + offsetTop;
            this.updateChildrenLayer(item, offsetLeft, offsetTop);
        });
    }

    /**
     * 左对齐
     */
    left(id: string) {
        const layer = this.framework.service.flatLayer.getLayer(id, true);
        const oldLayer = cloneDeep(layer);
        const positions: IPointVariant[] = [];
        const map = new Map<string, IPointVariant[]>();
        (layer.children || []).forEach((item) => {
            const pos = getRotatePosition(item);
            positions.push(...pos);
            map.set(item.id, pos);
        });
        if (!positions.length) return;
        const minPoint = minBy(positions, (pos) => round(pos.x)) as IPointVariant;
        const newLayerPositions: IPoint[] = [];
        map.forEach((point, id) => {
            if (id === minPoint.id) {
                newLayerPositions.push(...point);
                return;
            }
            const min = minBy(point, (pos) => round(pos.x)) as IPoint;
            const offsetX = min.x - minPoint.x;
            const offsetPoints = point.map((i) => {
                return { x: i.x - offsetX, y: i.y };
            });
            newLayerPositions.push(...offsetPoints);
            const item = find(layer.children, (i) => i.id === id) as ILayerProps;
            const oldLeft = item.left;
            const newLeft = item.left - offsetX;
            const offX = newLeft - oldLeft;
            this.updateChildrenLayer(item, offX);
            item.left = newLeft;
        });
        this.updateGroupLayer(layer, newLayerPositions);
        this.framework.service.history.write(oldLayer, getTrackFunc('left'));
    }

    /**
     * 右对齐
     */
    right(id: string) {
        const layer = this.framework.service.flatLayer.getLayer(id, true);
        const oldLayer = cloneDeep(layer);
        const positions: IPointVariant[] = [];
        const map = new Map<string, IPointVariant[]>();
        (layer.children || []).forEach((item) => {
            const pos = getRotatePosition(item);
            positions.push(...pos);
            map.set(item.id, pos);
        });
        if (!positions.length) return;
        const maxPoint = maxBy(positions, (pos) => round(pos.x)) as IPointVariant;
        const newLayerPositions: IPoint[] = [];
        map.forEach((point, id) => {
            if (id === maxPoint.id) {
                newLayerPositions.push(...point);
                return;
            }
            const max = maxBy(point, (pos) => round(pos.x)) as IPoint;
            const offsetX = max.x - maxPoint.x;
            const offsetPoints = point.map((i) => {
                return { x: i.x - offsetX, y: i.y };
            });
            newLayerPositions.push(...offsetPoints);
            const item = find(layer.children, (i) => i.id === id) as ILayerProps;
            const oldLeft = item.left;
            const newLeft = item.left - offsetX;
            const offX = newLeft - oldLeft;
            this.updateChildrenLayer(item, offX);
            item.left = newLeft;
        });
        this.updateGroupLayer(layer, newLayerPositions);
        this.framework.service.history.write(oldLayer, getTrackFunc('right'));
    }

    /**
     * 顶部对齐
     */
    top(id: string) {
        const layer = this.framework.service.flatLayer.getLayer(id, true);
        const oldLayer = cloneDeep(layer);
        const positions: IPointVariant[] = [];
        const map = new Map<string, IPointVariant[]>();
        (layer.children || []).forEach((item) => {
            const pos = getRotatePosition(item);
            positions.push(...pos);
            map.set(item.id, pos);
        });
        if (!positions.length) return;
        const minPoint = minBy(positions, (pos) => round(pos.y)) as IPointVariant;
        const newLayerPositions: IPoint[] = [];
        map.forEach((point, id) => {
            if (id === minPoint.id) {
                newLayerPositions.push(...point);
                return;
            }
            const min = minBy(point, (pos) => round(pos.y)) as IPoint;
            const offsetY = min.y - minPoint.y;
            const offsetPoints = point.map((i) => {
                return { x: i.x, y: i.y - offsetY };
            });
            newLayerPositions.push(...offsetPoints);
            const item = find(layer.children, (i) => i.id === id) as ILayerProps;
            const oldTop = item.top;
            const newTop = item.top - offsetY;
            const offY = newTop - oldTop;
            this.updateChildrenLayer(item, 0, offY);
            item.top = newTop;
        });
        this.updateGroupLayer(layer, newLayerPositions);
        this.framework.service.history.write(oldLayer, getTrackFunc('top'));
    }

    /**
     * 底部对齐
     */
    bottom(id: string) {
        const layer = this.framework.service.flatLayer.getLayer(id, true);
        const oldLayer = cloneDeep(layer);
        const positions: IPointVariant[] = [];
        const map = new Map<string, IPointVariant[]>();
        (layer.children || []).forEach((item) => {
            const pos = getRotatePosition(item);
            positions.push(...pos);
            map.set(item.id, pos);
        });
        if (!positions.length) return;
        const maxPoint = maxBy(positions, (pos) => round(pos.y)) as IPointVariant;
        const newLayerPositions: IPoint[] = [];
        map.forEach((point, id) => {
            if (id === maxPoint.id) {
                newLayerPositions.push(...point);
                return;
            }
            const max = maxBy(point, (pos) => round(pos.y)) as IPoint;
            const offsetY = max.y - maxPoint.y;
            const offsetPoints = point.map((i) => {
                return { x: i.x, y: i.y - offsetY };
            });
            newLayerPositions.push(...offsetPoints);
            const item = find(layer.children, (i) => i.id === id) as ILayerProps;
            const oldTop = item.top;
            const newTop = item.top - offsetY;
            const offY = newTop - oldTop;
            this.updateChildrenLayer(item, 0, offY);
            item.top = newTop;
        });
        this.updateGroupLayer(layer, newLayerPositions);
        this.framework.service.history.write(oldLayer, getTrackFunc('bottom'));
    }

    /**
     * 水平居中
     */
    verticalCenter(id: string) {
        const layer = this.framework.service.flatLayer.getLayer(id, true);
        const oldLayer = cloneDeep(layer);
        const resList: IPoint[] = [];
        const { left, top, width, height } = layer;
        const groupLayerCenter: IPoint = { x: width / 2 + left, y: height / 2 + top };
        (layer.children || []).forEach((item, index) => {
            const { left, top, width, height } = item;
            const layerCenter: IPoint = { x: width / 2 + left, y: height / 2 + top };
            resList.push({
                x: pointsDistance(layerCenter, groupLayerCenter),
                y: index,
            });
        });
        if (!resList.length) return;
        const minRes = minBy(resList, (item) => round(item.x));
        if (!minRes) return;
        const baseLayer = layer.children[minRes.y] as ILayerProps;
        const { left: l, top: t, width: w, height: h } = baseLayer;
        const baseCenter: IPoint = { x: w / 2 + l, y: h / 2 + t };
        const positions: IPoint[] = [];
        (layer.children || []).forEach((item) => {
            const newTop = baseCenter.y - item.height / 2;
            this.updateChildrenLayer(item, 0, newTop - item.top);
            item.top = newTop;
            positions.push(...getRotatePosition(item));
        });
        this.updateGroupLayer(layer, positions);
        this.framework.service.history.write(oldLayer, getTrackFunc('verticalCenter'));
    }

    /**
     * 垂直居中
     */
    horizontalCenter(id: string) {
        const layer = this.framework.service.flatLayer.getLayer(id, true);
        const oldLayer = cloneDeep(layer);
        const resList: IPoint[] = [];
        const { left, top, width, height } = layer;
        const groupLayerCenter: IPoint = { x: width / 2 + left, y: height / 2 + top };
        (layer.children || []).forEach((item, index) => {
            const { left, top, width, height } = item;
            const layerCenter: IPoint = { x: width / 2 + left, y: height / 2 + top };
            resList.push({
                x: pointsDistance(layerCenter, groupLayerCenter),
                y: index,
            });
        });
        if (!resList.length) return;
        const minRes = minBy(resList, (item) => round(item.x));
        if (!minRes) return;
        const baseLayer = layer.children[minRes.y] as ILayerProps;
        const { left: l, top: t, width: w, height: h } = baseLayer;
        const baseCenter: IPoint = { x: w / 2 + l, y: h / 2 + t };
        const positions: IPoint[] = [];
        (layer.children || []).forEach((item) => {
            const newLeft = baseCenter.x - item.width / 2;
            this.updateChildrenLayer(item, newLeft - item.left);
            item.left = newLeft;
            positions.push(...getRotatePosition(item));
        });
        this.updateGroupLayer(layer, positions);
        this.framework.service.history.write(oldLayer, getTrackFunc('horizontalCenter'));
    }

    /**
     * 水平分布，组内有3个以上图层时生效
     */
    horizontalDistribution(id: string) {
        const layer = this.framework.service.flatLayer.getLayer(id, true);
        const oldLayer = cloneDeep(layer);
        const children = layer.children || [];
        if (children.length <= 2) return;
        const resList: IPointVariant[] = [];
        // 过滤组套组不能使用分布
        (layer.children || []).forEach((item, index) => {
            const pos = getRotatePosition(item);
            const point = minBy(pos, (p) => round(p.x)) as IPoint;
            resList.push({
                x: point.x,
                y: index,
                id: item.id,
            });
        });
        const sortRes = sortBy(resList, (item) => item.x);
        const step = (sortRes[sortRes.length - 1].x - sortRes[0].x) / (sortRes.length - 1);
        const min = sortRes[0].x;
        sortRes.forEach((point, index) => {
            const item = children[point.y];
            const newX = min + index * step;
            const offX = newX - point.x;
            this.updateChildrenLayer(item, offX);
            item.left = item.left + offX;
        });
        const positions: IPoint[] = [];
        (layer.children || []).forEach((item) => {
            const pos = getRotatePosition(item);
            positions.push(...pos);
        });
        this.updateGroupLayer(layer, positions);
        this.framework.service.history.write(oldLayer, getTrackFunc('horizontalDistribution'));
    }

    /**
     * 垂直分布，组内有3个以上图层时生效
     */
    verticalDistribution(id: string) {
        const layer = this.framework.service.flatLayer.getLayer(id, true);
        const oldLayer = cloneDeep(layer);
        const children = layer.children || [];
        if (children.length <= 2) return;
        const resList: IPointVariant[] = [];
        (layer.children || []).forEach((item, index) => {
            const pos = getRotatePosition(item);
            const point = minBy(pos, (p) => round(p.y)) as IPoint;
            resList.push({
                x: point.y,
                y: index,
                id: item.id,
            });
        });
        const sortRes = sortBy(resList, (item) => item.x);
        const step = (sortRes[sortRes.length - 1].x - sortRes[0].x) / (sortRes.length - 1);
        const min = sortRes[0].x;
        sortRes.forEach((point, index) => {
            const item = children[point.y];
            const newY = min + index * step;
            const offY = newY - point.x;
            this.updateChildrenLayer(item, 0, offY);
            item.top = item.top + offY;
        });
        const positions: IPoint[] = [];
        (layer.children || []).forEach((item) => {
            const pos = getRotatePosition(item);
            positions.push(...pos);
        });
        this.updateGroupLayer(layer, positions);
        this.framework.service.history.write(oldLayer, getTrackFunc('verticalDistribution'));
    }
}
